import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

interface PlayNoticeContextValue {
  showPlayNotice: (title?: string) => void;
}

const PlayNoticeContext = createContext<PlayNoticeContextValue | null>(null);

export function usePlayNotice(): PlayNoticeContextValue {
  const ctx = useContext(PlayNoticeContext);
  if (!ctx) {
    return { showPlayNotice: () => undefined };
  }
  return ctx;
}

export function PlayNoticeProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");

  const showPlayNotice = useCallback((name?: string) => {
    setTitle(name || "");
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <PlayNoticeContext.Provider value={{ showPlayNotice }}>
      {children}
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-background-50/80 ng-animate-in"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-sm bg-background-100 border border-background-300 rounded-lg p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500/15 text-primary-500">
              <i className="ri-movie-2-line text-3xl"></i>
            </div>
            <h3 className="mt-4 text-lg font-heading text-foreground-950">敬请期待视频播放功能</h3>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              {title ? `《${title}》` : "当前内容"}的在线播放功能正在开发中，
              本站目前仅提供影视内容展示与推荐，感谢你的支持与耐心等待。
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-5 w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </PlayNoticeContext.Provider>
  );
}